/**
 * API endpoints para Scheduling Resources y Exceptions
 * Base URL: /api/scheduling/
 */

import { GET, POST, PUT, DELETE } from "../config";

// ==================== RESOURCES ====================

/**
 * Listar recursos de agenda
 * @param {object} filters - Filtros (calendar, branch, resource_type, is_active)
 * @returns {Promise} Lista de recursos
 */
export const listResources = async (filters = {}) => {
  let url = "scheduling/admin/resources/?page_size=50";

  if (filters.calendar) {
    url += `&calendar=${filters.calendar}`;
  }

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.resource_type) {
    url += `&resource_type=${filters.resource_type}`;
  }

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un recurso por ID
 * @param {string|number} id - ID del recurso
 * @returns {Promise} Datos del recurso
 */
export const getResource = async (id) => {
  const response = await GET(`scheduling/admin/resources/${id}/`);
  return response.data;
};

/**
 * Crear un recurso
 * @param {object} data - Datos del recurso
 * @returns {Promise} Recurso creado
 */
export const createResource = async (data) => {
  const response = await POST("scheduling/admin/resources/", data);
  return response.data;
};

/**
 * Actualizar un recurso
 * @param {string|number} id - ID del recurso
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Recurso actualizado
 */
export const updateResource = async (id, data) => {
  const response = await PUT(`scheduling/admin/resources/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un recurso
 * @param {string|number} id - ID del recurso
 * @returns {Promise}
 */
export const deleteResource = async (id) => {
  const response = await DELETE(`scheduling/admin/resources/${id}/`);
  return response.data;
};

// ==================== EXCEPTIONS ====================

/**
 * Listar excepciones de calendario
 * @param {object} filters - Filtros (calendar, exception_type, date_from, date_to)
 * @returns {Promise} Lista de excepciones
 */
export const listExceptions = async (filters = {}) => {
  let url = "scheduling/admin/exceptions/?page_size=50";

  if (filters.calendar) {
    url += `&calendar=${filters.calendar}`;
  }

  if (filters.exception_type) {
    url += `&exception_type=${filters.exception_type}`;
  }

  if (filters.date_from) {
    url += `&start_date__gte=${filters.date_from}`;
  }

  if (filters.date_to) {
    url += `&end_date__lte=${filters.date_to}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener una excepción por ID
 * @param {string|number} id - ID de la excepción
 * @returns {Promise} Datos de la excepción
 */
export const getException = async (id) => {
  const response = await GET(`scheduling/admin/exceptions/${id}/`);
  return response.data;
};

/**
 * Crear una excepción
 * @param {object} data - Datos de la excepción
 * @returns {Promise} Excepción creada
 */
export const createException = async (data) => {
  const response = await POST("scheduling/admin/exceptions/", data);
  return response.data;
};

/**
 * Actualizar una excepción
 * @param {string|number} id - ID de la excepción
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Excepción actualizada
 */
export const updateException = async (id, data) => {
  const response = await PUT(`scheduling/admin/exceptions/${id}/`, data);
  return response.data;
};

/**
 * Eliminar una excepción
 * @param {string|number} id - ID de la excepción
 * @returns {Promise}
 */
export const deleteException = async (id) => {
  const response = await DELETE(`scheduling/admin/exceptions/${id}/`);
  return response.data;
};

// ==================== DASHBOARD ====================

/**
 * Obtener dashboard de scheduling
 * @param {number} branchId - ID de la sucursal
 * @param {object} params - Parámetros (date_from, date_to)
 * @returns {Promise} Datos del dashboard
 */
export const getDashboard = async (branchId, params = {}) => {
  let url = `scheduling/admin/dashboard/?branch=${branchId}`;

  if (params.date_from) {
    url += `&date_from=${params.date_from}`;
  }

  if (params.date_to) {
    url += `&date_to=${params.date_to}`;
  }

  const response = await GET(url);
  return response.data;
};

// ==================== EXPORT ====================

const schedulingResourcesApi = {
  // Resources
  listResources,
  getResource,
  createResource,
  updateResource,
  deleteResource,
  // Exceptions
  listExceptions,
  getException,
  createException,
  updateException,
  deleteException,
  // Dashboard
  getDashboard,
};

export default schedulingResourcesApi;
